import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal } from "./Reveal";
import { IMAGES } from "@/lib/images";

const HOTSPOTS = [
    { name: "Découpe en U Coccygienne", detail: "Évite tout contact direct avec la zone douloureuse", pos: { top: "30%", left: "50%" } },
    { name: "Contour Ergonomique Cuisses", detail: "Maintient une circulation sanguine optimale sans engourdissement", pos: { top: "56%", left: "24%" } },
    { name: "Base Antidérapante Grip-Lock™", detail: "Reste parfaitement en place sur fauteuil, chaise ou siège auto", pos: { top: "78%", left: "62%" } },
    { name: "Mousse Viscoélastique Thermo-sensible", detail: "S’adapte à votre morphologie sans jamais s’affaisser", pos: { top: "14%", left: "28%" } },
];

export default function Anatomy() {
    const [active, setActive] = useState(0);
    return (
        <section id="anatomie" className="py-20 lg:py-32 bg-cream">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <Reveal className="relative order-1">
                    <div className="relative overflow-hidden rounded-[2rem] shadow-xl shadow-sage/15">
                        <img
                            data-testid="anatomy-product-image"
                            src={IMAGES.productDetail}
                            alt="Structure ergonomique du coussin Ergocox"
                            className="w-full aspect-[4/3] object-cover"
                        />
                        {HOTSPOTS.map((h, i) => (
                            <button
                                key={i}
                                data-testid={`hotspot-${i}`}
                                aria-label={h.name}
                                onClick={() => setActive(i)}
                                className="absolute -translate-x-1/2 -translate-y-1/2"
                                style={{ top: h.pos.top, left: h.pos.left }}
                            >
                                <span className={`relative grid place-items-center w-8 h-8 rounded-full ${active === i ? "bg-terra text-white" : "bg-cream/90 text-sage border border-sage/30"} shadow-lg transition-colors`}>
                                    {active !== i && <span className="absolute inset-0 rounded-full bg-terra/50 hotspot-ring" />}
                                    <Plus size={16} strokeWidth={2.5} className="relative" />
                                </span>
                            </button>
                        ))}
                    </div>
                </Reveal>
                <div className="order-2">
                    <Reveal>
                        <p className="text-xs uppercase font-mono tracking-[0.25em] text-sage-mid">Ingénierie Orthopédique</p>
                        <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink leading-[1.1]">
                            Chaque courbe a une <span className="italic text-sage">raison anatomique.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-9 flex flex-col gap-2">
                        {HOTSPOTS.map((h, i) => (
                            <Reveal key={i} delay={i * 0.08}>
                                <button
                                    data-testid={`anatomy-item-${i}`}
                                    onClick={() => setActive(i)}
                                    className={`w-full text-left rounded-2xl border p-5 transition-all duration-300 ${
                                        active === i
                                            ? "bg-sage text-cream border-sage shadow-lg shadow-sage/20"
                                            : "bg-white border-sage/15 hover:border-sage/40"
                                    }`}
                                >
                                    <span className="flex items-center justify-between gap-4">
                                        <span className="font-semibold text-base">
                                            <span className="font-mono text-xs mr-3 opacity-60">0{i + 1}</span>
                                            {h.name}
                                        </span>
                                        <Plus size={16} className={`shrink-0 transition-transform duration-300 ${active === i ? "rotate-45" : ""}`} />
                                    </span>
                                    <AnimatePresence initial={false}>
                                        {active === i && (
                                            <motion.p
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                                className="overflow-hidden text-sm text-cream/80 mt-3 pl-9"
                                            >
                                                {h.detail}
                                            </motion.p>
                                        )}
                                    </AnimatePresence>
                                </button>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
